import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';

import { AnnouncementPanel, Loader } from '@innovamat/glimmer-components';
import type { Classroom } from '@innovamat/glow-api-client';

import { useRegionParameters } from '../../contents';
import { useDigitalPracticeAnnouncement } from '../hooks/use-digital-practice-announcement';

const LoadingPanel = styled.div`
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.border['border-subtle'].value};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 66px;
`;

type DigitalPracticeAnnouncementProps = {
  classroom?: Classroom;
  digitalPracticeFirstDayOfWeek: string;
  digitalPracticeFirstHourOfWeek: string;
  showHourDropdownPractice?: boolean;
};

function DigitalPracticeAnnouncement({
  classroom,
  digitalPracticeFirstDayOfWeek,
  digitalPracticeFirstHourOfWeek,
  showHourDropdownPractice = false,
}: DigitalPracticeAnnouncementProps): JSX.Element | null {
  const { t } = useTranslation();
  const { regionParameters } = useRegionParameters({});

  const {
    currentWeekPeriod,
    formatDate,
    formatTime,
    hasChanges,
    isLoading,
    nextOccurrence,
    timezone,
  } = useDigitalPracticeAnnouncement({
    classroom,
    digitalPracticeFirstDayOfWeek,
    digitalPracticeFirstHourOfWeek,
    showHourDropdownPractice,
  });

  if (isLoading && !hasChanges) {
    return (
      <LoadingPanel>
        <Loader size="sm" color="black" />
      </LoadingPanel>
    );
  }

  if (!currentWeekPeriod) return null;

  return hasChanges ? (
    <AnnouncementPanel
      key={digitalPracticeFirstDayOfWeek}
      text={
        <>
          <Trans
            i18nKey="digitalPractice.modifyDigitalPracticeModal.informativeText3"
            components={{
              strong: <strong />,
            }}
            values={{
              day: `<strong>${
                nextOccurrence ? formatDate(nextOccurrence) : ''
              }</strong>`,
              hour: `<strong>${formatTime({
                clock: regionParameters?.clock,
              })}</strong>`,
            }}
          />{' '}
          {t('digitalPractice.modifyDigitalPracticeModal.informativeText4')}
        </>
      }
      canClose={false}
      type="info"
    />
  ) : (
    <AnnouncementPanel
      key={currentWeekPeriod.start.date}
      text={
        <>
          <Trans
            i18nKey="digitalPractice.modifyDigitalPracticeModal.informativeText1"
            components={{
              strong: <strong />,
            }}
            values={{
              day1: `<strong>${
                currentWeekPeriod.start
                  ? formatDate(
                      DateTime.fromSQL(currentWeekPeriod.start.date, {
                        zone: currentWeekPeriod.start.timezone,
                      }).toJSDate(),
                      timezone
                    )
                  : ''
              }</strong>`,
              hour1: `<strong>${formatTime({
                date: DateTime.fromSQL(currentWeekPeriod.start.date || '', {
                  zone: currentWeekPeriod.start.timezone,
                }).toJSDate(),
                clock: regionParameters?.clock,
                timezone,
              })}</strong>`,
              day2: `<strong>${
                currentWeekPeriod?.end
                  ? formatDate(
                      DateTime.fromSQL(currentWeekPeriod.end.date!, {
                        zone: currentWeekPeriod.end.timezone!,
                      }).toJSDate(),
                      timezone
                    )
                  : ''
              }</strong>`,
              hour2: `<strong>${formatTime({
                date: DateTime.fromSQL(currentWeekPeriod.end.date || '', {
                  zone: currentWeekPeriod.end.timezone,
                }).toJSDate(),
                clock: regionParameters?.clock,
                timezone,
              })}</strong>`,
            }}
          />
        </>
      }
      canClose={false}
      type="info-low"
    />
  );
}

export { DigitalPracticeAnnouncement };
